// useScrollFX.js
// Shared animation hooks for the Mean3 mobile-app-development page sections.
// Kept in one place so every section stays lightweight and consistent.
import { useEffect, useRef, useState } from 'react';

/**
 * Reveals an element with a fade/slide when it enters the viewport.
 * @param {number} threshold - IntersectionObserver threshold
 * @param {number} amount - px offset used by the caller for translate distance (informational)
 */
export const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

/**
 * Tracks pointer position relative to an element, normalized -1..1.
 * Useful for subtle tilt / parallax micro-interactions on hover.
 */
export const useTilt = (strength = 10) => {
  const ref = useRef(null);
  const [style, setStyle] = useState({});

  const onMouseMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setStyle({
      transform: `perspective(900px) rotateY(${px * strength}deg) rotateX(${-py * strength}deg) scale3d(1.02,1.02,1.02)`,
    });
  };

  const onMouseLeave = () => {
    setStyle({ transform: 'perspective(900px) rotateY(0deg) rotateX(0deg) scale3d(1,1,1)' });
  };

  return { ref, style, onMouseMove, onMouseLeave };
};

/**
 * Simple scroll-linked parallax offset for an element (moves slower/faster than scroll).
 * @param {number} speed - negative moves opposite scroll direction, positive moves with it
 */
export const useParallax = (speed = 0.15) => {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const el = ref.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          const centerDelta = rect.top + rect.height / 2 - window.innerHeight / 2;
          setOffset(centerDelta * speed);
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return [ref, offset];
};

/**
 * Animates a number counting up from 0 once the element is visible.
 */
export const useCountUp = (target, duration = 1200, visible = false) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!visible) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
      else setValue(target);
    };
    requestAnimationFrame(step);
  }, [visible, target, duration]);
  return value;
};
